import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, UserPlus, AlertCircle, Mail, Lock, User, CheckCircle } from "lucide-react";
import { supabase } from '@/lib/supabaseClient';

const Invite = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isAccountInvite = location.pathname.startsWith('/join-account');

    const [loading, setLoading] = useState(false);
    const [invitationValid, setInvitationValid] = useState(false);
    const [invitationData, setInvitationData] = useState<any | null>(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    // Validate invitation token and get email on page load
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setInvitationValid(false);
                return;
            }
            try {
                setLoading(true);
                const { data, error } = await supabase.rpc('validate_invitation_token', { p_token: token });

                if (data && !error) {
                    setInvitationValid(true);
                    setInvitationData(data)
                    setFormData(prev => ({ ...prev, email: data.email }));
                }
            } catch (error) {
                setInvitationValid(false);
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    const handleAcceptInvite = async () => {
        try {
            const { data, error } = await supabase.functions.invoke('accept-team-invite', {
                body: {
                    email: formData.email,
                    password: formData.password,
                    token,
                    fullName: `${formData.firstName} ${formData.lastName}`,
                }
            });
            if (error) throw error;
            if (data && data.success) {
                toast.success("Invitation accepted!");
                navigate("/auth/login");
            }

        } catch (err) {
            toast.error("Failed to accept invitation.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeclineInvite = async () => {
        setLoading(false);
        try {
            setLoading(true);
            const { data, error } = await supabase.rpc('decline_team_invitation', {
                p_invitation_id: invitationData.id
            });
            if (error) throw error;

            if (data && data.success) {
                toast.success("Invitation declined!");
                navigate("/dashboard");
            }
        } catch (err) {
            toast.error(err.message || "Failed to decline invitation.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        handleAcceptInvite();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Validating invitation...</p>
                    </CardContent>
                </Card>
            </div>
        )
    };

    if (!invitationValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                        <CardTitle className="text-red-600 dark:text-red-400">Invalid Invitation</CardTitle>
                        <CardDescription>
                            This invitation link is invalid or has expired.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground text-center">
                                Please contact the person who invited you to request a new invitation.
                            </p>
                            <Button
                                onClick={() => navigate('/')}
                                className="w-full"
                                variant="outline"
                            >
                                Go to Homepage
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        {invitationData.alreadyRegistered ? (
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        ) : (
                            <UserPlus className="h-6 w-6 text-primary" />
                        )}
                    </div>
                    <CardTitle>{isAccountInvite ? 'Join OG01' : `You're Invited to Join ${invitationData.team_name}`}</CardTitle>
                    <CardDescription>
                        {invitationData.alreadyRegistered ? (
                            <p>You’re already registered with{" "}<strong>{invitationData.email}</strong>. Do you want to accept this invitation to join{" "}<strong>{invitationData.team_name}</strong>?
                            </p>
                        ) : isAccountInvite ? (
                            "You've been invited to join the OG01 platform. Create your account to get started."
                        ) : (
                            <p><span className="capitalize">{invitationData.invited_by}</span>{" "}has invited you to become a member of the{" "}<strong>{invitationData.team_name}</strong> team. Create your account to accept the invitation and start collaborating.
                            </p>
                        )}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {invitationData.alreadyRegistered ? (
                        <div className="space-y-4 text-center">
                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={handleAcceptInvite}
                                    className="w-full"
                                >
                                    Accept Invitation
                                </Button>
                                <Button
                                    onClick={handleDeclineInvite}
                                    variant="outline"
                                    className="w-full"
                                >
                                    Decline
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">First Name</Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                placeholder="John"
                                                value={formData.firstName}
                                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                                required
                                                className="pl-9"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            name="lastName"
                                            type="text"
                                            placeholder="Doe"
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            className="pl-9"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            placeholder="Enter your password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            className="pl-9"
                                            minLength={8}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            placeholder="Confirm your password"
                                            value={formData.confirmPassword}
                                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                            required
                                            className="pl-9"
                                            minLength={8}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                {isAccountInvite && <div className="rounded-lg border p-3 bg-muted/30">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
                                        <div className="text-sm text-muted-foreground">
                                            <p className="font-medium">What happens next:</p>
                                            <ul className="mt-1 space-y-1 text-xs">
                                                <li>• Your account will be created with Premium access</li>
                                                <li>• You'll be added to the team that invited you</li>
                                                <li>• You can start using all OG01 features immediately</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>}
                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating Account...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="mr-2 h-4 w-4" />
                                            Create Account
                                        </>
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <p className="text-xs text-muted-foreground">
                                    By creating an account, you agree to our{' '}
                                    <a href="/terms" className="underline">Terms of Service</a>
                                    {' '}and{' '}
                                    <a href="/privacy" className="underline">Privacy Policy</a>
                                </p>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Invite; 